import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Lock, Radio, MapPin, Bell, Check, Twitter, MessageCircle, Link as LinkIcon } from "lucide-react";
import api from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

const HERO_IMG =
    "https://images.pexels.com/photos/6280898/pexels-photo-6280898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2000";

const easeOut = [0.16, 1, 0.3, 1];

function useCountdownTo(iso) {
    const target = new Date(iso);
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const diff = Math.max(0, target.getTime() - now.getTime());
    const s = Math.floor(diff / 1000);
    return {
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60,
        launched: diff === 0,
    };
}

function Reveal({ children, delay = 0, className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px -10% 0px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay, ease: easeOut }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

function MaskedLine({ children, delay = 0, className = "" }) {
    return (
        <span className="inline-block overflow-hidden align-bottom" style={{ lineHeight: 1, paddingRight: "0.08em", paddingBottom: "0.05em" }}>
            <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay, ease: easeOut }}
                className={`inline-block ${className}`}
            >
                {children}
            </motion.span>
        </span>
    );
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function Hero({ signedDays, entryCount, launchIso }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

    const status =
        signedDays == null
            ? ""
            : signedDays > 0
            ? `T-Minus ${signedDays} Day${signedDays === 1 ? "" : "s"} · Ninewells → NC500`
            : signedDays === 0
            ? "Day Zero · Wheels Up"
            : `Day +${Math.abs(signedDays)} of Flight`;

    return (
        <section
            ref={ref}
            className="relative min-h-[75svh] w-full overflow-hidden grain"
            data-testid="pilotlog-hero"
        >
            <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
                <img src={HERO_IMG} alt="Highland loch from above" className="w-full h-full object-cover slow-zoom-sw" />
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--sb-bg)]/60 via-[var(--sb-bg)]/25 to-[var(--sb-bg)]" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(50% 40% at 30% 55%, rgba(255,240,220,0.10) 0%, rgba(0,0,0,0) 60%)",
                    }}
                />
            </motion.div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-14 flex items-center justify-between text-xs tracking-[0.28em] uppercase text-[var(--sb-text-dim)]">
                <span className="flex items-center gap-2">
                    <Radio size={12} className="text-[var(--sb-brand)]" />
                    <MaskedLine delay={0.05}>Field Journal · Encrypted Transmissions</MaskedLine>
                </span>
                <span className="hidden sm:inline"><MaskedLine delay={0.15}>{status}</MaskedLine></span>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-16 lg:pt-32 lg:pb-24">
                <div className="overline mb-4">
                    <MaskedLine delay={0.1}>The Pilot&rsquo;s Log</MaskedLine>
                </div>
                <h1
                    className="font-serif font-light tracking-tighter"
                    style={{ fontSize: "clamp(2.6rem, 7.5vw, 7.5rem)", lineHeight: 0.94 }}
                    data-testid="pilotlog-title"
                >
                    <div>
                        <MaskedLine delay={0.15}>Notes from</MaskedLine>{" "}
                        <MaskedLine delay={0.3}>
                            <span className="italic text-[var(--sb-brand)]">the cockpit.</span>
                        </MaskedLine>
                    </div>
                </h1>
                <div className="mt-8 max-w-xl text-[var(--sb-text-dim)] text-lg leading-relaxed">
                    <MaskedLine delay={0.55}>
                        {entryCount === 0
                            ? "No transmissions yet. The log unlocks as the mission draws closer — one entry at a time."
                            : `${entryCount} transmission${entryCount === 1 ? "" : "s"} recovered. The rest arrive as the days close in.`}
                    </MaskedLine>
                </div>
                <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.28em] text-[var(--sb-text-muted)]">
                    <span className="w-10 h-px bg-[var(--sb-brand)]" />
                    Launch · {formatDate(launchIso)}
                </div>
            </div>
        </section>
    );
}

function SealedTransmission({ nextUnlock }) {
    const countdown = useCountdownTo(nextUnlock?.published_at || new Date().toISOString());

    return (
        <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="pilotlog-sealed">
            <Reveal>
                <div className="relative border border-[var(--sb-border)] bg-[var(--sb-surface)] p-10 lg:p-16 overflow-hidden">
                    <div
                        aria-hidden
                        className="absolute inset-0 opacity-[0.15]"
                        style={{
                            background:
                                "radial-gradient(60% 60% at 50% 40%, rgba(138,123,102,0.35) 0%, rgba(0,0,0,0) 60%)",
                        }}
                    />
                    <div className="absolute -top-10 -right-10 text-[13rem] font-serif italic leading-none text-[var(--sb-border)]">
                        01
                    </div>
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-8 text-[var(--sb-brand)]">
                            <Lock size={18} />
                            <span className="text-xs tracking-[0.32em] uppercase">Sealed Transmission</span>
                        </div>
                        <h2
                            className="font-serif tracking-tight leading-[1.02] mb-8"
                            style={{ fontSize: "clamp(2rem, 4.5vw, 3.6rem)" }}
                        >
                            The first entry is written.<br />
                            <span className="italic text-[var(--sb-brand)]">It hasn&rsquo;t opened yet.</span>
                        </h2>
                        <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed max-w-xl mb-10">
                            The Pilot&rsquo;s Log opens progressively as the NC500 draws closer. First transmission unlocks in:
                        </p>

                        {nextUnlock ? (
                            <div className="flex items-end gap-6 lg:gap-10 border-t border-[var(--sb-border)] pt-6" data-testid="sealed-countdown">
                                {[
                                    { label: "Days", value: countdown.days },
                                    { label: "Hrs", value: countdown.hours },
                                    { label: "Min", value: countdown.minutes },
                                    { label: "Sec", value: countdown.seconds },
                                ].map((u) => (
                                    <div key={u.label} className="flex flex-col items-start">
                                        <span
                                            className="font-serif text-[var(--sb-text)] tabular-nums leading-none"
                                            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
                                        >
                                            {String(u.value).padStart(2, "0")}
                                        </span>
                                        <span className="mt-2 text-[0.65rem] tracking-[0.32em] uppercase text-[var(--sb-text-muted)]">
                                            {u.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-[var(--sb-text-muted)] uppercase tracking-[0.25em]">
                                Next transmission — TBA
                            </p>
                        )}

                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link to="/start-here" className="btn-outline" data-testid="sealed-start-cta">
                                Read the Manifesto <ArrowRight size={16} />
                            </Link>
                            <a
                                href="https://gofund.me/da1365285"
                                target="_blank"
                                rel="noreferrer"
                                className="btn-primary"
                                data-testid="sealed-back-cta"
                            >
                                Back the Expedition <ArrowRight size={16} />
                            </a>
                        </div>
                    </div>
                </div>
            </Reveal>
        </section>
    );
}

function LogEntry({ entry, index }) {
    const flip = index % 2 === 1;
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

    const num = String(index + 1).padStart(2, "0");
    const hasImage = !!entry.image_url;

    return (
        <section
            className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 border-t border-[var(--sb-border)]"
            data-testid={`pilotlog-entry-${entry.id}`}
        >
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${flip && hasImage ? "lg:[&>*:first-child]:order-2" : ""}`}>
                {hasImage && (
                    <Reveal className="lg:col-span-5">
                        <div
                            ref={imgRef}
                            className="relative aspect-[3/4] overflow-hidden border border-[var(--sb-border)]"
                            style={{
                                clipPath:
                                    index % 2 === 0
                                        ? "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)"
                                        : "polygon(8% 0, 100% 0, 100% 100%, 0 100%, 0 8%)",
                            }}
                        >
                            <motion.img
                                src={entry.image_url}
                                alt={entry.title}
                                style={{ y }}
                                className="absolute inset-0 w-full h-[115%] object-cover will-change-transform"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)]/60 via-transparent to-transparent" />
                            <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between">
                                <span className="text-xs tracking-[0.28em] uppercase text-[var(--sb-text)]">Entry</span>
                                <span
                                    className="font-serif italic leading-none text-[var(--sb-text)]"
                                    style={{ fontSize: "clamp(3rem, 6vw, 5rem)" }}
                                >
                                    {num}
                                </span>
                            </div>
                        </div>
                    </Reveal>
                )}

                <div className={hasImage ? "lg:col-span-7" : "lg:col-span-12 max-w-4xl"}>
                    <Reveal>
                        <div className="flex items-center gap-4 mb-6 flex-wrap">
                            <span className="w-12 h-px bg-[var(--sb-brand)]" />
                            {entry.day_label && (
                                <span className="text-xs tracking-[0.28em] uppercase text-[var(--sb-brand)]">
                                    {entry.day_label}
                                </span>
                            )}
                            <span className="text-xs tracking-[0.28em] uppercase text-[var(--sb-text-muted)]">
                                {formatDate(entry.published_at)}
                            </span>
                            {entry.location && (
                                <span className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--sb-text-muted)]">
                                    <MapPin size={11} /> {entry.location}
                                </span>
                            )}
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h3
                            className="font-serif tracking-tight leading-[1.05] mb-8"
                            style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)" }}
                        >
                            {entry.title}
                        </h3>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="text-[var(--sb-text-dim)] text-lg leading-[1.85] whitespace-pre-line max-w-2xl">
                            {entry.body}
                        </div>
                    </Reveal>
                    {entry.mood && (
                        <Reveal delay={0.15}>
                            <div className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-[var(--sb-text-muted)]">
                                <span className="w-6 h-px bg-[var(--sb-brand)]" />
                                Conditions · {entry.mood}
                            </div>
                        </Reveal>
                    )}
                </div>
            </div>
        </section>
    );
}

function ShareMissionPanel() {
    const [copied, setCopied] = useState(false);
    const shareUrl = "https://dtscotslad.com/start-here";
    const shareText =
        "I'm on the flight deck for the NC500 — a 516-mile aerial expedition raising funds for the MS Society. Follow the mission:";

    const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch {
            // Fallback: create a hidden textarea (very rare path)
            const ta = document.createElement("textarea");
            ta.value = `${shareText} ${shareUrl}`;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch { /* ignore */ }
            document.body.removeChild(ta);
        }
    };

    const btnBase =
        "inline-flex items-center justify-center gap-2 border border-[var(--sb-border)] hover:border-[var(--sb-brand)] px-4 py-3 text-xs uppercase tracking-[0.22em] text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] transition-colors";

    return (
        <div className="mt-8 border-t border-[var(--sb-border)] pt-6" data-testid="share-mission-panel">
            <div className="overline mb-4">Multiply the mission</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <a
                    href={twitterHref}
                    target="_blank"
                    rel="noreferrer"
                    className={btnBase}
                    data-testid="share-twitter"
                >
                    <Twitter size={14} /> Share on X
                </a>
                <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    className={btnBase}
                    data-testid="share-whatsapp"
                >
                    <MessageCircle size={14} /> WhatsApp
                </a>
                <button
                    type="button"
                    onClick={copyLink}
                    className={btnBase}
                    data-testid="share-copy"
                >
                    {copied ? (<><Check size={14} className="text-[var(--sb-brand)]" /> Copied</>) : (<><LinkIcon size={14} /> Copy link</>)}
                </button>
            </div>
        </div>
    );
}

function LaunchAlertsForm() {
    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setError("That doesn't look like a valid email.");
            setState("error");
            return;
        }
        setState("loading");
        setError("");
        try {
            await api.post("/launch-alerts/subscribe", { email: trimmed, source: "pilots-log" });
            setState("success");
        } catch (err) {
            setError(err.response?.data?.detail || "Couldn't sign you up. Try again.");
            setState("error");
        }
    };

    return (
        <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32 border-t border-[var(--sb-border)]" data-testid="launch-alerts">
            <Reveal>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-3 text-[var(--sb-brand)] mb-4">
                            <Bell size={18} />
                            <span className="text-xs tracking-[0.32em] uppercase">Launch Alerts</span>
                        </div>
                        <h2
                            className="font-serif tracking-tight leading-[1.05] mb-6"
                            style={{ fontSize: "clamp(2rem, 4.2vw, 3.4rem)" }}
                        >
                            Get pinged the moment a new transmission unlocks.
                        </h2>
                        <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed">
                            No newsletter. No spam. Just one email per sealed entry — from the pre-flight checks all the way through Day +7 of the NC500.
                        </p>
                    </div>
                    <div className="lg:col-span-6">
                        {state === "success" ? (
                            <div className="border border-[var(--sb-brand)] bg-[var(--sb-surface)] p-8" data-testid="launch-alerts-success">
                                <div className="flex items-center gap-3 text-[var(--sb-brand)] mb-3">
                                    <Check size={20} />
                                    <span className="text-xs tracking-[0.32em] uppercase">Locked in</span>
                                </div>
                                <p className="font-serif text-2xl leading-tight text-[var(--sb-text)]">
                                    You&rsquo;re on the flight deck.
                                </p>
                                <p className="text-[var(--sb-text-dim)] mt-3">
                                    The moment the next transmission unlocks, you&rsquo;ll hear it from Mission Control.
                                </p>
                                <ShareMissionPanel />
                            </div>
                        ) : (
                            <form onSubmit={submit} className="border border-[var(--sb-border)] bg-[var(--sb-surface)] p-8" data-testid="launch-alerts-form">
                                <label htmlFor="launch-alerts-email" className="overline block mb-3">Your email</label>
                                <input
                                    id="launch-alerts-email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    inputMode="email"
                                    placeholder="pilot@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-line w-full mb-6"
                                    data-testid="launch-alerts-email"
                                />
                                <button
                                    type="submit"
                                    className="btn-primary w-full justify-center"
                                    disabled={state === "loading"}
                                    data-testid="launch-alerts-submit"
                                >
                                    {state === "loading" ? "Locking in…" : (<>Get transmission alerts <ArrowRight size={16} /></>)}
                                </button>
                                {state === "error" && (
                                    <p className="mt-4 text-sm text-red-400" data-testid="launch-alerts-error">{error}</p>
                                )}
                                <p className="mt-4 text-xs tracking-wider uppercase text-[var(--sb-text-muted)]">
                                    One email per unlock. Nothing else.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </Reveal>
        </section>
    );
}

export default function PilotLog() {
    usePageMeta({
        title: "The Pilot's Log — DTScotslad",
        description: "Progressive field journal from Mark Ireland's 516-mile NC500 aerial expedition. Transmissions unlock one at a time as the mission draws closer.",
        ogImage: HERO_IMG,
    });

    const [state, setState] = useState({
        loading: true,
        entries: [],
        nextUnlock: null,
        signedDays: null,
        launchIso: "2026-11-06T06:00:00+00:00",
    });

    useEffect(() => {
        api.get("/pilot-log")
            .then((r) => {
                setState({
                    loading: false,
                    entries: r.data.entries || [],
                    nextUnlock: r.data.next_unlock || null,
                    signedDays: r.data.signed_days_to_launch,
                    launchIso: r.data.launch_iso || "2026-11-06T06:00:00+00:00",
                });
            })
            .catch(() => setState((s) => ({ ...s, loading: false })));
    }, []);

    return (
        <div data-testid="pilotlog-page">
            <Hero
                signedDays={state.signedDays}
                entryCount={state.entries.length}
                launchIso={state.launchIso}
            />

            {state.entries.length === 0 && !state.loading && (
                <SealedTransmission nextUnlock={state.nextUnlock} />
            )}

            {state.entries.map((e, i) => (
                <LogEntry key={e.id} entry={e} index={i} />
            ))}

            {state.entries.length > 0 && state.nextUnlock && (
                <SealedTransmission nextUnlock={state.nextUnlock} />
            )}

            <LaunchAlertsForm />

            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 text-center border-t border-[var(--sb-border)]">
                <Reveal>
                    <div className="overline mb-4">Support the transmission</div>
                    <h3 className="font-serif tracking-tight leading-tight mb-6" style={{ fontSize: "clamp(1.8rem, 3.5vw, 3rem)" }}>
                        Every mile of the NC500 is fuel, batteries and hours in the air.
                    </h3>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="https://gofund.me/da1365285" target="_blank" rel="noreferrer" className="btn-primary" data-testid="pilotlog-back-cta">
                            Back the Expedition <ArrowRight size={16} />
                        </a>
                        <Link to="/subscription" className="btn-outline" data-testid="pilotlog-subscribe-cta">
                            Subscribe — £1.89 / mo
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
